import { useMemo } from 'react';

import { Grow, Popper, styled, useTheme } from '@mui/material';
// eslint-disable-next-line @nx/enforce-module-boundaries -- Module Federation singleton 키를 유지하려면 패키지 경로 import가 필요합니다.
import { usePopupContext } from '@kc-console/common-ui/singleton';

import { convertOriginToPlacement, resolvePlacement } from './getPlacementOrigins';
import { dropdownMenu2Classes } from './theme';

import type { ReactNode } from 'react';
import type { PopperProps } from '@mui/material';
import type { PropsWithRefObject } from '../../types';
import type { DropdownMenu2OriginProp, DropdownMenu2Placement } from './types';

// ============================================================================
// Types
// ============================================================================

export interface DropdownMenu2PopperProps extends PropsWithRefObject<HTMLDivElement> {
  /**
   * 드롭다운메뉴가 위치할 기준 요소입니다.
   */
  anchorEl: PopperProps['anchorEl'];
  /**
   * 드롭다운메뉴 내부에 렌더링할 컨텐츠입니다.
   */
  children: ReactNode;
  /**
   * 드롭다운메뉴의 표시 상태를 제어합니다.
   */
  open: boolean;
  /**
   * className을 전달합니다.
   */
  className?: string;
  /**
   * Portal 렌더링을 비활성화합니다.
   * @default false
   */
  disablePortal?: boolean;
  /**
   * 화면 가장자리 여백입니다.
   * @default 16
   */
  marginThreshold?: number;
  /**
   * Transition 완료 후 호출되는 콜백입니다.
   */
  onTransitionEntered?: () => void;
  /**
   * @deprecated Use placement instead.
   * 기존 origin 형식입니다. placement로 내부 변환됩니다.
   */
  origin?: DropdownMenu2OriginProp;
  /**
   * 드롭다운 위치를 결정하는 placement입니다.
   * - shorthand: "bottom-start", "top-end" 등 PopperPlacementType
   * - detail: { anchorOrigin: "bottom left", transformOrigin?: "top left" }
   * @default 'bottom-start'
   */
  placement?: DropdownMenu2Placement;
  /**
   * Popper root의 역할입니다.
   */
  role?: PopperProps['role'];
  /**
   * z-index 값입니다.
   * @default PopupContext 또는 theme.zIndex.contentPopup(1295)
   */
  zIndex?: number;
}

// ============================================================================
// Main Component
// ============================================================================

export default function DropdownMenu2Popper(props: DropdownMenu2PopperProps) {
  const {
    anchorEl,
    children,
    open,
    className,
    disablePortal = false,
    marginThreshold = 16,
    onTransitionEntered,
    origin,
    placement = 'bottom-start',
    ref,
    role,
    zIndex: zIndexProp,
  } = props;
  const theme = useTheme();
  const popupContext = usePopupContext();

  const zIndex = zIndexProp ?? popupContext?.zIndex ?? theme.zIndex.contentPopup;

  // ========== Placement 계산 ==========
  const resolvedPlacement = useMemo(() => {
    // origin이 전달되면 placement로 변환 (deprecated 경고)
    if (origin) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          '[DropdownMenu2] origin prop is deprecated. Use placement instead.\n' +
            'Example: placement="bottom-start" or placement={{ anchorOrigin: "bottom left" }}',
        );
      }
      const convertedPlacement = convertOriginToPlacement(origin);
      return resolvePlacement(convertedPlacement);
    }
    return resolvePlacement(placement);
  }, [origin, placement]);

  // transformOrigin을 CSS 문자열로 변환
  const transformOriginStyle = useMemo(() => {
    const { transformOrigin } = resolvedPlacement;
    if (transformOrigin) {
      return `${transformOrigin.horizontal} ${transformOrigin.vertical}`;
    }
    // 기본값: placement에 따라 계산 (fallback)
    return resolvedPlacement.popperPlacement.startsWith('bottom') ? 'left top' : 'left bottom';
  }, [resolvedPlacement]);

  return (
    <DropdownMenu2PopperRoot
      open={open}
      anchorEl={anchorEl}
      {...(role ? { role } : {})}
      placement={resolvedPlacement.popperPlacement}
      disablePortal={disablePortal}
      transition
      modifiers={[
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            padding: marginThreshold,
            altBoundary: false,
            altAxis: true,
            tether: true,
          },
        },
        {
          name: 'flip',
          enabled: true,
          options: {
            padding: marginThreshold,
          },
        },
      ]}
      style={{ zIndex }}
      className={className}
      onClick={e => e.stopPropagation()}
    >
      {({ TransitionProps }) => (
        <Grow
          {...TransitionProps}
          onEntered={onTransitionEntered}
          style={{
            transformOrigin: transformOriginStyle,
          }}
          timeout={0}
        >
          <DropdownMenu2PopperContent ref={ref}>{children}</DropdownMenu2PopperContent>
        </Grow>
      )}
    </DropdownMenu2PopperRoot>
  );
}

// ============================================================================
// Styled Components
// ============================================================================

const DropdownMenu2PopperRoot = styled(Popper, {
  target: dropdownMenu2Classes.root,
})`
  pointer-events: none;
  & > * {
    pointer-events: auto;
  }
`;

const DropdownMenu2PopperContent = styled('div')`
  display: contents;
`;
